import { Component, DestroyRef, HostListener, OnInit, Renderer2, computed, effect, inject, signal } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterModule, RouterOutlet } from '@angular/router';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzModalModule } from 'ng-zorro-antd/modal';

import { NzLayoutModule } from 'ng-zorro-antd/layout';

import { AsyncPipe, CommonModule } from '@angular/common';
import { ModalService } from '@core/services/modal.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { Observable } from 'rxjs';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { AuthenticatedUserDto } from '@shared/models/authenticated-user.dto';
import { environment } from '@env/environment';
import { en_GB, NZ_I18N } from 'ng-zorro-antd/i18n';
import { version } from '@env/version';
import { TranslateService } from '@core/services/translate.service';
import { AppLanguageService } from '@core/services/app-language.service';
import { TranslatePipe } from '@shared/pipes/translate.pipe';
import { MaintenanceScreenComponent } from '@shared/components/maintenance-screen/maintenance-screen.component';
import { AuthFacade } from '@core/facades/auth.facade';
import { ViewportService } from '@core/services/viewport.service';
import { RoleLevel } from '@shared/models/role.enum';
import { IEBMember } from '@shared/models/entities/member.interface';
import { MemberAvatarComponent } from '@shared/components/member-avatar/member-avatar.component';
import { UtilityDockComponent } from './shell/utility-dock/utility-dock.component';
import { UtilityDockStore } from './shell/utility-dock/utility-dock.store';
import { AssignedTasksStore } from './shell/utility-dock/tasks/assigned-tasks.store';
import { InboxStore } from './shell/utility-dock/inbox/inbox.store';
import { PushInboxStore } from '@core/services/push-inbox.store';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UI_TEXT } from '@app/constants/ui-text.constants';
import { UTILITY_DOCK } from '@app/constants/utility-dock.constants';
import type { UtilityDockTab } from '@app/constants/utility-dock.constants';
import { LoadingIndicatorService } from '@core/services/loading-indicator.service';
import { NavigationFeedbackStore } from '@core/services/navigation-feedback.store';
import { AnalyticsService } from '@core/services/analytics.service';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { FeedbackButtonComponent } from '@shared/components/feedback/feedback-button/feedback-button.component';
import { FilePreviewModalComponent } from '@shared/components/file-preview/file-preview-modal.component';
import { FileUploadTrayComponent } from '@shared/components/file-upload-tray/file-upload-tray.component';
import { MobileFilePreviewModalComponent } from './mobile/components/file-preview/mobile-file-preview-modal.component';
import { MobileSlowConnectionHintComponent } from './shared/components/mobile-slow-connection-hint/mobile-slow-connection-hint.component';
import { OverflowNavComponent, OverflowNavItem } from '@shared/components/overflow-nav/overflow-nav.component';
import { resolveDefaultLandingRoute } from '@shared/utils/default-landing-route';
import { FocusManagementService } from '@shared/services/focus-management.service';
import { MobileBackNavigationService } from '@core/services/mobile-back-navigation.service';
import { MobileOverlayBackService } from '@core/services/mobile-overlay-back.service';
import { NotificationsStore } from './shell/utility-dock/notifications/notifications.store';

@Component({
  providers: [
    ModalService,
    [
      { provide: NZ_I18N, useValue: en_GB }, // ✅ Monday-first baseline
    ],
  ],
  standalone: true,
  selector: 'app-root',
  imports: [
    IonApp,
    IonRouterOutlet,
    RouterModule,
    RouterOutlet,
    AsyncPipe,
    NzLayoutModule,
    CommonModule,
    NzModalModule,
    NzSpinModule,
    NzIconModule,
    NzMenuModule,
    NzButtonModule,
    NzBadgeModule,
    NzAvatarModule,
    NzDropDownModule,
    TranslatePipe,
    UtilityDockComponent,
    MemberAvatarComponent,
    FeedbackButtonComponent,
    FilePreviewModalComponent,
    FileUploadTrayComponent,
    MobileFilePreviewModalComponent,
    MobileSlowConnectionHintComponent,
    OverflowNavComponent,
    NzProgressModule,
    MaintenanceScreenComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly overlayContainer = inject(OverlayContainer);
  private readonly renderer = inject(Renderer2);
  private readonly auth = inject(AuthFacade);
  private readonly assignedTasks = inject(AssignedTasksStore);
  private readonly inboxStore = inject(InboxStore);
  private readonly pushInbox = inject(PushInboxStore);
  protected readonly utilityDock = inject(UtilityDockStore);
  protected readonly loadingIndicator = inject(LoadingIndicatorService);
  private readonly navigationFeedback = inject(NavigationFeedbackStore);
  protected readonly analytics = inject(AnalyticsService); // Initialize analytics tracking
  protected readonly viewport = inject(ViewportService);
  private readonly focusManagement = inject(FocusManagementService);
  private readonly notificationsStore = inject(NotificationsStore);
  protected readonly UI_TEXT = UI_TEXT;
  private readonly translate = inject(TranslateService);
  private readonly appLanguage = inject(AppLanguageService);
  protected readonly UTILITY_DOCK = UTILITY_DOCK;
  protected readonly resolveDefaultLandingRoute = resolveDefaultLandingRoute;
  readonly env = environment;
  readonly sha = version;
  protected readonly RoleLevel = RoleLevel;
  protected activeLocale = this.translate.locale;
  protected mobileMenuOpen = false;
  protected userMenuOpen = false;
  protected readonly hideHeaderAuth = signal(false);
  protected readonly hideHeader = signal(false);
  protected readonly hideAppChrome = signal(false);
  protected readonly utilitySections: UtilityDockTab[] = [
    UTILITY_DOCK.TAB.NOTIFICATIONS,
  ];

  title = '';

  isLoggedIn$: Observable<boolean>;
  isMemberExisting$: Observable<boolean>;
  roleValue$: Observable<number>;

  user$: Observable<AuthenticatedUserDto | null>;
  member$: Observable<IEBMember | null>;
  private member: IEBMember | null = null;

  private readonly isLoggedInSig = toSignal(this.auth.isLoggedIn$, { initialValue: false });
  private readonly roleValueSig = toSignal(this.auth.roleValue$, { initialValue: 0 });

  private readonly allNavItems: readonly (OverflowNavItem & { minRole: RoleLevel })[] = [
    { key: 'aufgaben', route: '/aufgaben', icon: 'check-square', labelKey: 'app.nav.aufgaben', labelFallback: 'Aufgaben', minRole: RoleLevel.Member },
    { key: 'kanban', route: '/kanban', icon: 'project', labelKey: 'app.nav.kanban', labelFallback: 'Kanban', minRole: RoleLevel.PartyLeader },
    { key: 'gantt', route: '/gantt', icon: 'bar-chart', labelKey: 'app.nav.gantt', labelFallback: 'Planung', minRole: RoleLevel.Member },
    { key: 'kampagnen', route: '/kampagnen', icon: 'flag', labelKey: 'app.nav.kampagnen', labelFallback: 'Kampagnen', minRole: RoleLevel.PartyLeader },
    { key: 'dateiablage', route: '/dateiablage', icon: 'folder-open', labelKey: 'app.nav.dateiablage', labelFallback: 'Dateiablage', minRole: RoleLevel.Member },
    { key: 'diskussionen', route: '/diskussionen', icon: 'comment', labelKey: 'app.nav.diskussionen', labelFallback: 'Diskussionen', minRole: RoleLevel.Member },
    { key: 'party-mgmt', route: '/party-mgmt', icon: 'control', labelKey: 'app.nav.partyMgmt', labelFallback: 'Parteiverwaltung', minRole: RoleLevel.PartyAdmin },
    { key: 'admin-users', route: '/admin/users', icon: 'crown', labelKey: 'app.nav.adminCenter', labelFallback: 'Admin Center', minRole: RoleLevel.GlobalAdmin },
  ];

  protected readonly primaryNavItems = computed<OverflowNavItem[]>(() => {
    if (!this.isLoggedInSig()) return [];
    const role = this.roleValueSig() ?? 0;
    return this.allNavItems
      .filter((item) => role >= item.minRole)
      .map(({ minRole: _minRole, ...rest }) => rest);
  });

  protected readonly unreadDiscussionCount = computed(() =>
    this.notificationsStore.unreadDiscussionCount(),
  );

  protected readonly navDotKeys = computed<string[]>(() =>
    this.unreadDiscussionCount() > 0 ? ['diskussionen'] : [],
  );

  protected onPrimaryNavClick(): void {
    this.utilityDock.closeAll();
    this.closeMobileMenu();
  }

  constructor() {
    inject(MobileBackNavigationService);
    inject(MobileOverlayBackService);

    this.isLoggedIn$ = this.auth.isLoggedIn$;
    this.isMemberExisting$ = this.auth.isMemberExisting$;
    this.roleValue$ = this.auth.roleValue$;
    this.user$ = this.auth.user$;
    this.member$ = this.auth.member$;
    void this.appLanguage.applyLocale(this.activeLocale);

    // set app title from translations
    this.title = this.translate.instant('common.brand.electa');

    // Toggle `ionic-mobile-mode` on <body>. Ionic's structure.css globally
    // locks the body (`position: fixed; overflow: hidden`) so its full-screen
    // shell works on mobile. On desktop we use a regular nz-layout with
    // body-level scrolling, so we must opt out of that lock when not mobile.
    // See styles.scss for the corresponding override block.
    effect(() => {
      const isMobile = this.viewport.isMobile();
      const cls = 'ionic-mobile-mode';
      if (typeof document === 'undefined') return;
      document.body.classList.toggle(cls, isMobile);

      const overlayElement = this.overlayContainer.getContainerElement();
      if (isMobile) {
        this.renderer.removeClass(overlayElement, 'desktop-overlay-button-pilot');
      } else {
        this.renderer.addClass(overlayElement, 'desktop-overlay-button-pilot');
      }
    });
  }

  protected utilityDockButtonAriaLabel(): string {
    const inbox = this.assignedTasks.itemsVm().length;
    const todos = this.inboxStore.openAssignedTasksCount();
    const mentions = this.utilityDock.mentionsOrAssignmentsCount();
    const parts: string[] = [];
    if (inbox > 0) parts.push(this.translate.instant('utilityDock.counts.inbox', { count: inbox }));
    if (todos > 0) parts.push(this.translate.instant('utilityDock.counts.todos', { count: todos }));
    if (mentions > 0) parts.push(this.translate.instant('utilityDock.counts.mentions', { count: mentions }));
    if (parts.length === 0) return this.UI_TEXT.UTILITY_DOCK.TITLE;
    return `${this.UI_TEXT.UTILITY_DOCK.TITLE}: ${parts.join(', ')}`;
  }

  protected sectionLabel(section: UtilityDockTab): string {
    if (section === UTILITY_DOCK.TAB.NOTIFICATIONS) return this.translate.instant('activity.notifications.eyebrow');
    return this.UI_TEXT.COMMON.INBOX;
  }

  protected sectionIcon(section: UtilityDockTab): string {
    if (section === UTILITY_DOCK.TAB.NOTIFICATIONS) return 'bell';
    return 'inbox';
  }

  protected sectionCount(section: UtilityDockTab): number {
    if (section === UTILITY_DOCK.TAB.NOTIFICATIONS) {
      return this.utilityDock.unreadNotificationsCount() + this.pushInbox.unreadCount();
    }
    return this.inboxStore.openAssignedTasksCount();
  }

  protected isUtilitySectionActive(section: UtilityDockTab): boolean {
    if (section === UTILITY_DOCK.TAB.INBOX) {
      return this.utilityDock.inboxOpen();
    }

    return this.utilityDock.notificationsOpen();
  }

  protected isPrivateTasksActive(): boolean {
    return this.utilityDock.privateTasksOpen();
  }

  protected privateTasksCount(): number {
    return this.inboxStore.filteredItems().filter((item) => {
      const kind = String(item.kind ?? '').trim().toLowerCase();
      if (kind) {
        return kind === 'private';
      }

      const hasNotificationSource = String(item.sourceNotificationId ?? '').trim().length > 0;
      const hasEntityContext = String(item.entityId ?? '').trim().length > 0;
      return !hasNotificationSource && !hasEntityContext;
    }).length;
  }

  protected openSection(section: UtilityDockTab): void {
    this.closeMobileMenu();

    if (section === UTILITY_DOCK.TAB.INBOX) {
      this.utilityDock.openInbox();
    } else if (section === UTILITY_DOCK.TAB.NOTIFICATIONS) {
      this.utilityDock.openNotifications();
    }
  }

  protected openPrivateTasks(): void {
    this.utilityDock.openPrivateTasks();
    this.closeMobileMenu();
  }

  ngOnInit(): void {
    this.pushInbox.init();
    this.auth.hydrateFromToken();
    this.updateLayoutFlags();
    this.router.events
      .pipe(
        filter((evt): evt is NavigationEnd => evt instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.updateLayoutFlags());

    this.translate.translations$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const locale = this.translate.locale;
        if (locale === this.activeLocale) return;
        this.activeLocale = locale;
      });
    
    // Track route loading for progress indicator
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((evt) => {
        if (evt instanceof NavigationStart) {
          if (this.shouldClearFocusForNavigation(evt.url)) {
            this.focusManagement.clearActiveElement();
          }
          this.loadingIndicator.startLoading('route');

          // Drive the slow-connection feedback ladder for *every* navigation,
          // not just tab-bar taps. Skip same-URL navigations (refresh, query
          // changes) and programmatic redirects we don't want to surface.
          const currentUrl = this.router.url;
          const isSameUrl = evt.url === currentUrl;
          const trigger = evt.navigationTrigger;
          const isUserDriven =
            trigger === 'imperative' ||
            trigger === 'popstate' ||
            trigger === 'hashchange';
          if (!isSameUrl && isUserDriven) {
            const kind = trigger === 'popstate' ? 'back' : 'forward';
            this.navigationFeedback.start(evt.url, kind);
          }
          return;
        }

        if (evt instanceof NavigationEnd || evt instanceof NavigationCancel || evt instanceof NavigationError) {
          this.loadingIndicator.stopLoading('route');
        }
      });
    
    this.member$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((member) => {
        this.member = member ?? null;
      });
  }

  private shouldClearFocusForNavigation(nextUrl: string): boolean {
    return this.stripQueryAndHash(nextUrl) !== this.stripQueryAndHash(this.router.url);
  }

  private stripQueryAndHash(url: string): string {
    return url.split(/[?#]/, 1)[0] || '/';
  }

  async logout(): Promise<void> {
    await wait();
    this.auth.logout();
    this.router.navigateByUrl('/', { replaceUrl: true });
  }

  async hardLogout(): Promise<void> {
    await wait();
    await this.auth.hardLogout();
  }

  async switchLocale(locale: string): Promise<void> {
    if (locale === this.activeLocale) {
      this.closeMobileMenu();
      return;
    }
    this.activeLocale = await this.appLanguage.applyLocale(locale);
    this.closeMobileMenu();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 768) {
      this.closeMobileMenu();
    }
  }

  protected toggleMobileMenu(): void {
    const nextState = !this.mobileMenuOpen;

    if (nextState) {
      this.utilityDock.closeAll();
    }

    this.mobileMenuOpen = nextState;
  }

  protected closeMobileMenu(): void {
    this.mobileMenuOpen = false;
  }

  protected openAboutModal(): void {
    // Modal will show footer content (brand, environment, language switcher)
    // Can be expanded with additional info as needed
    // Implementation: Use NzModalService to create modal with footer content
  }

  protected onAvatarError(): void {
    // Avatar component handles image error internally
  }

  protected getFirstName(fullName?: string): string | undefined {
    if (!fullName) return undefined;
    return fullName.split(' ')[0] || undefined;
  }

  protected getLastName(fullName?: string): string | undefined {
    if (!fullName) return undefined;
    const parts = fullName.split(' ');
    return parts.length > 1 ? parts[parts.length - 1] : undefined;
  }

  private updateLayoutFlags(): void {
    let active: ActivatedRoute | null = this.route;
    let hideHeaderAuth = false;
    let hideHeader = false;
    let hideAppChrome = false;

    while (active) {
      const data = active.snapshot.data as Record<string, unknown>;
      hideHeaderAuth = hideHeaderAuth || Boolean(data['hideHeaderAuth']);
      hideHeader = hideHeader || Boolean(data['hideHeader']);
      hideAppChrome = hideAppChrome || Boolean(data['hideAppChrome']);
      active = active.firstChild ?? null;
    }

    this.hideHeaderAuth.set(hideHeaderAuth);
    this.hideHeader.set(hideHeader);
    this.hideAppChrome.set(hideAppChrome);
  }
}

function wait() {
  return new Promise((resolve) => setTimeout(resolve, 300));
}
